import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { CompanyUsersQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { CompanyUserFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'
import { Badge as RoleBadge } from '@/components/role/badge'
import { format } from 'date-fns'

interface Props extends TableProps {
  createable?: boolean
  updateable?: boolean
  filters?: CompanyUserFilters
}

export const Table = ({ pageSize = 10, filters = {}, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<CompanyUsersQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openEdit, setOpenEdit } = useTable({ pageInfo: data?.companyUsers.pageInfo })
  const [companyUserId, setCompanyUserId] = useState<string>()

  const editCompanyUser = (id: string) => {
    if (!updateable) return

    setCompanyUserId(id)
    setOpenEdit(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<CompanyUserFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Invite</Button> : undefined}
        filters={[]}
        headings={[{ name: 'User' }, { name: 'Accepted' }, { name: 'Role' }, { name: 'Added by' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.companyUsers.nodes = dedupe([
              ...previousData.companyUsers.nodes,
              ...fetchMoreResult.companyUsers.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.companyUsers.nodes.map(companyUser => (
          {
            id: companyUser.id,
            columns: [
              companyUser.user.name,
              companyUser.acceptedAt
                ? format(companyUser.acceptedAt, 'KK:mm dd/MM/yyyy')
                : <span className="text-gray-500">Not accepted yet</span>,
              (
                <div className="flex flex-wrap gap-2">
                  <RoleBadge role={{ name: companyUser.role }} />
                </div>
              ),
              companyUser.initiatedBy?.name || 'System'
            ]
          }
        ))}
        onRowClick={editCompanyUser}
      />
      { companyUserId && <EditDialog id={companyUserId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
